/**
 * Проверяет, подходит ли сущность под условия бонусной программы
 * @param entityType
 * @param entity
 * @param level
 * @param operationType
 * @return {boolean}
 */
export const isValidEntityForBPLevel = ({
  entityType,
  entity,
  level,
  operationType,
}) => {
  const settings = level.params[operationType].study;

  const errorCondition = (operationType === 'discount' && !settings.isApply)
    || (operationType === 'points' && !settings.isCanPay);

  if (errorCondition) {
    return false;
  }

  const {
    subscriptionIds = [],
    subscriptionGroupIds = [],
    classIds = [],
    courseIds = [],
  } = settings;

  switch (entityType) {
    case 'sub': {
      const sub = entity;
      return subscriptionIds.includes(sub.id)
        || subscriptionGroupIds.includes(sub.subscription_group_id)
        || subscriptionIds.includes(-1);
    }
    case 'class': {
      const cl = entity;
      return classIds.includes(cl.id)
        || courseIds.includes(cl.courseId)
        || courseIds.includes(-1);
    }

    default:
      return false;
  }
};

/**
 * Рассчитывает скидку по настройкам бонусной программы
 * @param entityType
 * @param entity
 * @param initDiscount
 * @param level
 * @return {{discount}|{discount, maxDiscount}|{discount}|{discount: *}}
 */
export const calculateDiscount = ({
  entityType,
  entity,
  initDiscount,
  level,
}) => {
  if (!level) {
    return { discount: initDiscount };
  }
  const { discount } = level.params;

  if (!entity) {
    return { discount: initDiscount };
  }

  const entityBpParams = (entity.bonusProgram || {});

  const discountParams = discount.study;

  // Скидка выключениа
  if (!discountParams.isApply) {
    return { discount: initDiscount };
  }

  // Сущности нет в настройках БП
  if (!isValidEntityForBPLevel({
    entityType,
    entity,
    level,
    operationType: 'discount',
  })) {
    return { discount: initDiscount };
  }

  // Определяем скидку
  const bpLevelDiscount = discountParams.percents;
  const {
    maxDiscount,
    restrictBP,
  } = entityBpParams;

  if (!restrictBP || !maxDiscount) {
    return initDiscount > bpLevelDiscount
      ? { discount: initDiscount }
      : { discount: bpLevelDiscount };
  }

  const result = {
    discount: initDiscount,
    maxDiscount,
  };

  // Если скидка клиента превышает максимум в сущности БП
  // или скидка в БП превышает максимум в сущности БП
  if (initDiscount >= maxDiscount
    || (initDiscount <= bpLevelDiscount && bpLevelDiscount >= maxDiscount)) {
    result.discount = maxDiscount;

    // Если скидка клиента меньше, чем скидка в уровне БП
  } if (initDiscount <= bpLevelDiscount && bpLevelDiscount <= maxDiscount) {
    result.discount = bpLevelDiscount;
  }

  return result;
};

export const getBonusesSum = ({
  level,
  entity,
  bpParamsBlock = 'study',
  price,
  balance,
}) => {
  const { bonusProgram: entityBpSettings = {} } = entity;
  const levelSettings = level.params.points[bpParamsBlock];

  const {
    isCanPayPart,
    sumLimit: levelSumLimit,
    percentsLimit,
  } = levelSettings;

  const {
    restrictBP,
    sumLimit,
  } = entityBpSettings;

  // Считаем доступную сумму для списания
  let totalSum = price;

  if (isCanPayPart) {
    if (percentsLimit) {
      totalSum *= (percentsLimit / 100);
    }
    if (levelSumLimit) {
      totalSum = levelSumLimit < totalSum ? levelSumLimit : totalSum;
    }
  }

  if (restrictBP && sumLimit) {
    totalSum = sumLimit < totalSum ? sumLimit : totalSum;
  }

  if (totalSum >= balance) {
    return balance;
  }

  return +totalSum.toFixed(2);
};

/**
 * Рассчитывает сумму бонусов для списания
 * @param entityType - тип сущности sub | class
 * @param entity - сущность
 * @param level - уровень БП
 * @param balance - баланс бонусов
 * @param price - нужно оплатить
 * @param initPrice - изначальная цена
 * @param bonusesPaid - оплачено бонусами
 * @return {number|null}
 */
export const getAvailableBonusesSum = ({
  entityType,
  entity,
  level,
  balance,
  price,
  initPrice,
  bonusesPaid = 0,
}) => {
  const isValid = isValidEntityForBPLevel({
    level,
    entity,
    entityType,
    operationType: 'points',
  });

  if (!isValid) {
    return null;
  }

  if (!balance) {
    return 0;
  }

  const bpParamsBlock = 'study';

  if (initPrice === price) {
    const bonusesSumForCurrentPrice = getBonusesSum({
      level,
      entity,
      bpParamsBlock,
      price,
      balance,
    });

    return +bonusesSumForCurrentPrice.toFixed(2);
  }

  // Считаем максимум для оплаты бонусами
  const bonusesSumForInitPrice = getBonusesSum({
    level,
    entity,
    bpParamsBlock,
    price: initPrice,
    balance: 999999999,
  });

  const availableBonusesToPay = bonusesSumForInitPrice - bonusesPaid;

  let resultSum = 0;
  if (availableBonusesToPay <= 0) {
    resultSum = 0;
  } else if (availableBonusesToPay >= price) {
    resultSum = price > balance ? balance : price;
  } else if (availableBonusesToPay >= balance) {
    resultSum = balance;
  } else {
    resultSum = availableBonusesToPay;
  }

  return +resultSum.toFixed(2);
};
